import React, { useEffect, useState } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";

/**
 * ScrollHints — subtle top/bottom "more above / more below" indicators that
 * appear only when there IS more content in that direction. Keeps new users
 * oriented on long dashboard-style pages.
 *
 * Fixed to viewport. Auto-hides when at edges. Clicking either arrow scrolls
 * smoothly to that end.
 */
export default function ScrollHints({ testIdPrefix = "scroll-hint" }) {
  const [hasAbove, setHasAbove] = useState(false);
  const [hasBelow, setHasBelow] = useState(false);

  useEffect(() => {
    const compute = () => {
      const y = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      // 24px buffer so tiny overflows don't flicker.
      setHasAbove(y > 24);
      setHasBelow(max - y > 24);
    };
    compute();
    window.addEventListener("scroll", compute, { passive: true });
    window.addEventListener("resize", compute);
    // Recompute when React re-mounts content (poll a few times over 2s).
    const t1 = setTimeout(compute, 300);
    const t2 = setTimeout(compute, 1000);
    const t3 = setTimeout(compute, 2000);
    return () => {
      window.removeEventListener("scroll", compute);
      window.removeEventListener("resize", compute);
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
    };
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const scrollToBottom = () => window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });

  return (
    <>
      {hasAbove && (
        <button
          type="button"
          onClick={scrollToTop}
          data-testid={`${testIdPrefix}-top`}
          aria-label="Scroll to top"
          className="fixed top-20 right-4 md:right-6 z-40 flex items-center gap-1.5 rounded-full border border-cyan/40 bg-obsidian/85 backdrop-blur-md px-2.5 py-1.5 text-cyan hover:bg-cyan/[0.12] hover:border-cyan/70 transition-all shadow-[0_0_20px_-6px_rgba(75,156,255,0.6)] animate-in fade-in slide-in-from-top-2 duration-200"
        >
          <ChevronUp className="h-3.5 w-3.5" />
          <span className="mono text-[10px] tracking-[0.22em] uppercase hidden sm:inline">More above</span>
        </button>
      )}
      {hasBelow && (
        <button
          type="button"
          onClick={scrollToBottom}
          data-testid={`${testIdPrefix}-bottom`}
          aria-label="Scroll to bottom"
          className="fixed bottom-6 right-4 md:right-6 z-40 flex items-center gap-1.5 rounded-full border border-cyan/40 bg-obsidian/85 backdrop-blur-md px-2.5 py-1.5 text-cyan hover:bg-cyan/[0.12] hover:border-cyan/70 transition-all shadow-[0_0_20px_-6px_rgba(75,156,255,0.6)] animate-in fade-in slide-in-from-bottom-2 duration-200"
        >
          <span className="mono text-[10px] tracking-[0.22em] uppercase hidden sm:inline">More below</span>
          <ChevronDown className="h-3.5 w-3.5 animate-bounce" style={{ animationDuration: "2.4s" }} />
        </button>
      )}
    </>
  );
}
